import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Button, Grid, IconButton, TextField, Tooltip, withStyles } from '@material-ui/core'
import { Close, InfoOutlined } from '@material-ui/icons'
import { DatePicker } from '@material-ui/pickers'
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField'

import { acGetCurrentProductTable, acGetProductTables, acGetProducts, acInsertProductTableData } from '../../class/actions'
import { Label, Loader, SelectField } from '../default'

const FormProductTable = ({
  classes,
  currentTable, loadingCurrentTable,
  products, loadingProducts,
  productTables,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const PLAN_TABLE_PREFIX = 'Tabela de Produtos'

  const [productTableName, setProductTableName] = useState(PLAN_TABLE_PREFIX)
  const [productTableNameError, setProductTableNameError] = useState(null)
  const [productTableDescription, setProductTableDescription] = useState('')
  const [validUntil, setValidUntil] = useState(null)
  const [productTable, setProductTable] = useState([null])
  const [version, setVersion] = useState(null)
  const [versions, setVersions] = useState([])

  useEffect(() => {
    if (!currentTable)
      dispatch(acGetCurrentProductTable())

    if (!products)
      dispatch(acGetProducts())

    if (!productTables || !productTables.length)
      dispatch(acGetProductTables())
  }, [dispatch, products, currentTable, productTables])

  useEffect(() => {
    if (productTables && productTables.length) {
      // Get all unique version values
      setVersions([...new Set(productTables.map(item => item.version))])
    }
  }, [productTables])

  useEffect(() => {
    if (!loadingCurrentTable && currentTable && productTable.length <= 1) {
      setProductTable(currentTable.products.map(product => ({
        product_id: product.product_id,
        price: product.ProductPrices.price
      })))
    }
  }, [currentTable, loadingCurrentTable, productTable])

  useEffect(() => {
    dispatch(acInsertProductTableData({
      name: productTableName,
      description: productTableDescription,
      valid_until: validUntil ? validUntil.toISOString().split('T')[0] : null,
      products: productTable,
      version: version
    }))
  }, [dispatch, validUntil, productTableName, productTableDescription, productTable, version])

  const setProductEntryPrice = (price, index) => {
    setProductTable(productTable.map((item, idx) => index === idx ? item === null ? {
      product_id: null,
      price: price
    } : { ...item, price: price } : item))
  }

  const setProductEntryValue = (value, index) => {
    setProductTable(productTable.map((item, idx) => index === idx ? item === null ? {
      product_id: value,
      price: 0
    } : { ...item, product_id: value } : item))
  }

  const removeProductTableEntry = index => {
    setProductTable(productTable.filter((_, idx) => idx !== index))
  }

  const expandEntryList = () => {
    if (!productTable.some(item => item === null))
      setProductTable(productTable.concat(null))
  }

  const validateProductTableName = value => {
    setProductTableName(value)
    if (!value.includes(PLAN_TABLE_PREFIX)) {
      setProductTableNameError(`O nome da tabela deve conter "${PLAN_TABLE_PREFIX}"`)
      return
    }

    if (!value.length) {
      setProductTableNameError('O nome da tabela não pode ser vazio!')
      return
    }

    setProductTableNameError(null)
  }

  const renderProductEntry = (product, index) => {
    return <Grid key={index} container spacing={2}>
      {
        (!loadingProducts && products) &&
        <>
          <Grid item xs={7}>
            <SelectField id={index.toString()}
              items={products.map(p => ({
                name: p.name,
                value: p.product_id
              }))}
              value={product === null ? '' : productTable[index].product_id}
              onChange={e => setProductEntryValue(e.target.value, index)} />
          </Grid>
          <Grid item xs={3}>
            <CurrencyTextField
              variant='filled'
              label={t('modals.products.price')}
              value={product === null ? 0 : productTable[index].price}
              currencySymbol="R$"
              outputFormat="number"
              decimalCharacter=","
              digitGroupSeparator="."
              onChange={(e, v) => setProductEntryPrice(parseFloat(v), index)}
              style={{ width: '100%' }} />
          </Grid>
          {
            index !== 0 &&
            <Grid item xs={2}>
              <Tooltip title={t('default.removeItemFromList')}>
                <IconButton onClick={() => removeProductTableEntry(index)}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Grid>
          }
        </>
      }
    </Grid>
  }

  return (
    <Grid container spacing={2} className={classes.grid}>

      <Grid item xs={12}>
        <TextField
          required
          variant='filled'
          type="text"
          label={t('forms.productTable.tableName')}
          value={productTableName}
          onChange={(e) => validateProductTableName(e.target.value)}
          error={productTableNameError ? true : false}
          helperText={productTableNameError ? productTableNameError : null}
          style={{ width: '100%' }} />
      </Grid>

      <Grid item xs={12}>
        <TextField
          variant='filled'
          type="text"
          label={t('forms.productTable.tableDescription')}
          value={productTableDescription}
          onChange={(e) => setProductTableDescription(e.target.value)}
          style={{ width: '100%' }} />
      </Grid>

      <Grid item xs={6}>
        <div className={classes.tooltipContainer}>
          <DatePicker
            label={t('forms.productTable.validUntil')}
            format="dd/MM/yyyy"
            inputVariant="outlined"
            value={validUntil}
            onChange={e => setValidUntil(e)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <Tooltip title={t('forms.productTable.validUntilTooltip')} style={{ marginLeft: '10px' }}>
          <InfoOutlined fontSize='small' />
        </Tooltip>
      </Grid>

      <Grid item xs={6}>
        <div className={classes.tooltipContainer}>
          <SelectField
            label={t('forms.productTable.version')}
            items={versions.map(v => ({
              name: (currentTable && currentTable.version) === v ? `${v} (${t('forms.customerRegister.default')})` : v,
              value: v
            }))}
            value={version}
            onChange={e => setVersion(e.target.value)} />
        </div>
        <Tooltip title={(
          <div>
            Diferenças entre as versões:<br />
            Versão 1:<br />
            - Consulta personalizada habilitada<br />
            Versão 2:<br />
            - Consulta personalizada desabilitada<br />
            - Melhorias na interface de usuário no painel esquerdo
          </div>
        )} style={{ marginLeft: '10px' }}>
          <InfoOutlined fontSize='small' />
        </Tooltip>
      </Grid>

      <Grid item xs={12}>
        <Label bold>
          {t('forms.productTable.products')}
        </Label>
      </Grid>

      <Grid item xs={12}>
        {
          (!loadingProducts && !loadingCurrentTable) ?
            productTable.map((p, index) => renderProductEntry(p, index))
            : <Loader />
        }
      </Grid>

      <Grid item xs={6}>
        <Button onClick={expandEntryList} style={{ width: '100%', backgroundColor: '#ddd', color: 'black' }}>
          {t('modals.productTable.expandEntryList')}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Label v="caption" caption component="span" style={{ textAlign: 'center' }}>*{t('modals.productTable.warning')}</Label>
      </Grid>

    </Grid>
  )
}

const styles = theme => ({
  grid: {
    display: 'flex',
    flex: 1
  },
  tooltipContainer: {
    display: 'inline-block',
    width: 'calc(100% - 35px)',
  }
})

const mapStateToProps = ({ ReducerProductTable, ReducerProduct }) => ({
  currentTable: ReducerProductTable.currentTable,
  currentTableError: ReducerProductTable.currentTableError,
  loadingCurrentTable: ReducerProductTable.loadingCurrentTable,

  productTables: ReducerProductTable.productTables,
  productTablesError: ReducerProductTable.productTablesError,
  loadingProductTables: ReducerProductTable.loadingProductTables,

  products: ReducerProduct.products,
  productsError: ReducerProduct.productsError,
  loadingProducts: ReducerProduct.loadingProducts
})

export default connect(mapStateToProps)(withStyles(styles)(FormProductTable))